import { createFileRoute } from "@tanstack/react-router";
import { useLocation } from "@tanstack/react-router";
import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import type { ClientPaginatedResponse } from "api-types/common/commonResponses";
import type { ClientFeatureFlagDefinition, ClientFeatureFlagGroup } from "api-types/featureFlags/featureFlagsResponses";
import type { ClientFlagData } from "api-types/metrics/metricsResponses";
import type { ClientFeatureFlagStats } from "api-types/metrics/metricsResponses";
import type { ClientUserHydrated } from "api-types/users/usersResponses";
import { DOC_URLS, FALLBACK_VALUE, WEB_API_BASE_PATH } from "common/constants";
import type { FeatureName } from "common/constants/features";
import { assertIsNotNullOrUndefined, ensureDefined } from "common/utils/assertOfType";
import { getClientEnvironment } from "common/utils/env";
import { filterList } from "common/utils/search";
import { camelCaseToTitleCase } from "common/utils/strings";
import featureFlagOverviewStyles from "lib/components/admin/builders/AdminBuilderDetail.module.scss";
import featureFlagListStyles from "lib/components/admin/builders/AdminBuilderList.module.scss";
import AdminPage from "lib/components/admin/builders/AdminPage";
import AdminTableHeader from "lib/components/admin/builders/AdminTableHeader";
import BuilderFeatureFlagsList from "lib/components/admin/features/BuilderFeatureFlagsList";
import FeatureFlagDefinitionEditor from "lib/components/admin/features/FeatureFlagDefinitionEditor";
import FeatureFlagEventsList from "lib/components/admin/features/FeatureFlagEventsList";
import FeatureFlagGroupBulkControls from "lib/components/admin/features/FeatureFlagGroupBulkControls";
import type { FeatureFlagGroupBulkControlsSection } from "lib/components/admin/features/FeatureFlagGroupBulkControls";
import FeatureFlagGroupEditor from "lib/components/admin/features/FeatureFlagGroupEditor";
import { settingToDisplayValue, mapFeatureStatusToDisplayTextAndColor } from "lib/components/admin/features/helpers";
import UserFeatureFlagsList from "lib/components/admin/features/UserFeatureFlagsList";
import BuilderPortalEmptyCTA from "lib/components/builders/BuilderPortalEmptyCTA";
import { getFeatureFlagGroupsURL, getFeatureFlagsURL } from "lib/endpointHelpers";
import { useFetch, usePaginatedFetch } from "lib/fetcher";
import { useAppRouterParams } from "lib/routeHelpers";
import { userIsSystemAdmin } from "lib/security/shared";
import { AdminBuilderContext } from "lib/state/AdminBuilderContextProvider";
import { useUserContext } from "lib/state/UserContextProvider";
import { deleteFeatureFlag, deleteFeatureFlagGroup } from "lib/storage/featureFlagStorage";
import { useContext, useEffect, useMemo, useState } from "react";
import type { ReactElement } from "react";
import Button from "ui/buttons/Button";
import Capsule from "ui/components/Capsule";
import ConfirmDeleteModal from "ui/components/ConfirmDeleteModal";
import HorizontalRule from "ui/components/HorizontalRule";
import Link from "ui/components/Link";
import Loading from "ui/components/Loading";
import Modal from "ui/components/Modal";
import TabbedMenu from "ui/components/TabbedMenu";
import type { OptionProps } from "ui/forms/Select";
import Select from "ui/forms/Select";
import { useAppNavigate } from "ui/hooks/useAppNavigate";
import useMediaQuery from "ui/hooks/useMediaQuery";
import useTabbedMenu from "ui/hooks/useTabbedMenu";
import IconTriangle from "ui/icons/IconTriangle";
import SplitFlex from "ui/layouts/SplitFlex";
import SearchInput from "ui/search/SearchInput";
import { ConfigTable } from "ui/tables/DataTable";
import type { Column } from "ui/tables/DataTable/types";
import { Message } from "ui/text/Message";
import TextWithTooltipIcon from "ui/text/TextWithTooltipIcon";
import withAuthentication from "../../components/withAuthentication";
import styles from "./features.module.scss";

type AdminFeatureFlagsPageTab = "settings" | "builders" | "users" | "metrics" | "history";
type AdminFeatureFlagGroupTab = "settings" | FeatureFlagGroupBulkControlsSection | "members";

type FeatureFlagListRow =
  | {
      type: "group";
      id: string;
      group: ClientFeatureFlagGroup;
      visibleMembers: ClientFeatureFlagGroup["members"];
    }
  | { type: "feature"; id: string; definition: ClientFeatureFlagDefinition };

const METRIC_CARD_CONFIG: Array<{
  key:
    | "effectiveEnabledBuilders"
    | "effectiveEnabledUsers"
    | "explicitEnabledBuilders"
    | "explicitEnabledUsers"
    | "inheritedDefaultBuilders"
    | "inheritedDefaultUsers";
  label: string;
  tooltip: string;
}> = [
  {
    key: "effectiveEnabledBuilders",
    label: "Effective Enabled Builders",
    tooltip:
      "Builders currently receiving this flag as enabled. Includes explicit builder enables and builders inheriting from a default-enabled flag.",
  },
  {
    key: "effectiveEnabledUsers",
    label: "Effective Enabled Users",
    tooltip:
      "Users currently receiving this flag as enabled. Includes explicit user enables and users inheriting from a default-enabled flag.",
  },
  {
    key: "explicitEnabledBuilders",
    label: "Explicit Enabled Builders",
    tooltip: "Builders with an explicit flag value of Enabled saved on the builder record.",
  },
  {
    key: "explicitEnabledUsers",
    label: "Explicit Enabled Users",
    tooltip: "Users with an explicit flag value of Enabled saved on the user record.",
  },
  {
    key: "inheritedDefaultBuilders",
    label: "Inheriting Default Builders",
    tooltip: "Builders with no explicit builder override for this flag, so they inherit the default-enabled behavior.",
  },
  {
    key: "inheritedDefaultUsers",
    label: "Inheriting Default Users",
    tooltip: "Users with no explicit user override for this flag, so they inherit the default-enabled behavior.",
  },
];

function getFeatureDisplayName(featureDefinition: Pick<ClientFeatureFlagDefinition, "key" | "displayName">): string {
  return featureDefinition.displayName ?? camelCaseToTitleCase(featureDefinition.key);
}

/**
 * Whether *any* super admin (not just allowlisted editors) can manage per-user/builder
 * overrides for a flag in this stage. Intentionally stage-based rather than derived from
 * `definition.canManageOverrides`: that field reflects the current viewer's access, while
 * this drives an informational message shown to flag editors about what other staff can do.
 * Must stay in sync with `canUserManageFlagOverrides` in featureFlagsService.
 */
function canAnyHigharcStaffManageOverrides(stage: string): boolean {
  return stage === "open_beta" || stage === "general_availability";
}

function AdminFeatureFlagsPage() {
  const navigate = useAppNavigate();
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [environmentFilter, setEnvironmentFilter] = useState<string>("all");
  const [stalenessFilter, setStalenessFilter] = useState<string>("all");
  const [lifecycleFilter, setLifecycleFilter] = useState<string>("active");
  const [showDeleteRetiredModal, setShowDeleteRetiredModal] = useState(false);
  const [showNewGroupModal, setShowNewGroupModal] = useState(false);
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [groupTab, setGroupTab] = useState<AdminFeatureFlagGroupTab>("settings");
  const [isDeletingRetired, setIsDeletingRetired] = useState(false);
  const [isDeletingGroup, setIsDeletingGroup] = useState(false);
  const [mobilePane, setMobilePane] = useState<"list" | "detail">("list");
  const { feature, group } = useAppRouterParams();
  const { user } = useUserContext();
  const mobile = useMediaQuery("mobile");
  const { selectedFeature, setSelectedFeature } = useContext(AdminBuilderContext);
  const { server: environment } = getClientEnvironment();
  const { tab, menuItems } = useTabbedMenu<AdminFeatureFlagsPageTab>([
    "settings",
    "builders",
    "users",
    "metrics",
    "history",
  ]);
  const groupMenuItems = useMemo(() => {
    const groupTabs: Array<{ tab: AdminFeatureFlagGroupTab; text: string }> = [
      { tab: "settings", text: "Settings" },
      { tab: "builders", text: "Builders" },
      { tab: "users", text: "Users" },
      { tab: "members", text: "Member Flags" },
    ];

    return groupTabs.map((item) => {
      return {
        text: item.text,
        selected: groupTab === item.tab,
        onClick: () => setGroupTab(item.tab),
      };
    });
  }, [groupTab]);
  const {
    data: users,
    loading: usersLoading,
    mutate: mutateUsers,
    setSearch: setUserSearch,
    orderBy,
    setOrderBy,
    pagination,
    setPagination,
  } = usePaginatedFetch<ClientUserHydrated>(`${WEB_API_BASE_PATH}/users`);
  const {
    data: builders,
    loading: buildersLoading,
    mutate: mutateBuilders,
  } = useFetch<ClientPaginatedResponse<ClientBuilder>>(`${WEB_API_BASE_PATH}/builders?limit=1000`);
  const {
    data: featureDefinitions,
    loading: featureDefinitionsLoading,
    mutate: mutateFeatureDefinitions,
  } = useFetch<ClientFeatureFlagDefinition[]>(getFeatureFlagsURL({ includeRetired: lifecycleFilter === "retired" }));
  const {
    data: featureFlagGroups,
    loading: featureFlagGroupsLoading,
    mutate: mutateFeatureFlagGroups,
  } = useFetch<ClientFeatureFlagGroup[]>(getFeatureFlagGroupsURL());
  const { data: flagData, loading: flagDataLoading } = useFetch<ClientFlagData>(`${WEB_API_BASE_PATH}/metrics/flags`);

  const leftPaneLoading =
    (featureDefinitionsLoading && !featureDefinitions) || (featureFlagGroupsLoading && !featureFlagGroups);
  const rightPaneLoading =
    featureDefinitionsLoading ||
    (tab === "builders" && buildersLoading) ||
    (tab === "users" && usersLoading) ||
    (tab === "metrics" && flagDataLoading);

  const selectedFeatureDefinition = useMemo(() => {
    if (!selectedFeature) {
      return null;
    }

    return featureDefinitions?.find((item) => item.key === selectedFeature) ?? null;
  }, [featureDefinitions, selectedFeature]);

  const selectedFeatureFlagGroup = useMemo(() => {
    if (!selectedGroupId) {
      return null;
    }

    return featureFlagGroups?.find((group) => group.id === selectedGroupId) ?? null;
  }, [featureFlagGroups, selectedGroupId]);

  const selectedFeatureMetrics: ClientFeatureFlagStats | null = useMemo(() => {
    if (!selectedFeature) {
      return null;
    }

    return flagData?.featureFlags?.[selectedFeature] ?? null;
  }, [flagData, selectedFeature]);

  const selectedFeatureGroupSiblingKeys = useMemo(() => {
    if (!selectedFeature) {
      return [];
    }

    const siblingKeys = new Set<FeatureName>();
    for (const group of featureFlagGroups ?? []) {
      if (!group.members.some((member) => member.featureKey === selectedFeature)) {
        continue;
      }

      for (const member of group.members) {
        if (member.featureKey !== selectedFeature) {
          siblingKeys.add(member.featureKey);
        }
      }
    }

    return [...siblingKeys];
  }, [featureFlagGroups, selectedFeature]);

  useEffect(() => {
    if (mobile && (selectedFeature || selectedGroupId)) {
      setMobilePane("detail");
      return;
    }

    if (mobile && !selectedFeature && !selectedGroupId) {
      setMobilePane("list");
      return;
    }

    if (!mobile) {
      setMobilePane("list");
    }
  }, [mobile, selectedFeature, selectedGroupId]);

  const selectedFeatureEnvironmentSetting = useMemo(() => {
    if (!selectedFeatureDefinition) {
      return "Default";
    }

    const environmentOverride = selectedFeatureDefinition.environmentOverrides.find(
      (item) => item.environment === environment,
    )?.value;

    if (environmentOverride === true) {
      return "Enabled";
    }

    if (environmentOverride === false) {
      return "Disabled";
    }

    return "Default";
  }, [environment, selectedFeatureDefinition]);

  const featureColumns: Column<ClientFeatureFlagDefinition>[] = useMemo(
    () => [
      { header: "Feature", accessor: (definition) => getFeatureDisplayName(definition), bold: true, wrap: true },
      {
        header: "Default Value",
        accessor: ({ defaultValue }) => settingToDisplayValue(defaultValue),
        wrap: true,
      },
      {
        header: "Status",
        accessor: ({ stage }) => stage,
        cell: ({ row }) => {
          const mapping = mapFeatureStatusToDisplayTextAndColor(row.original.stage);
          return <Capsule label={mapping.text} size="small" backgroundColor={mapping.color} color="white" />;
        },
        wrap: true,
      },
    ],
    [],
  );

  const listColumns: Column<FeatureFlagListRow>[] = useMemo(
    () => [
      {
        header: "Feature",
        accessor: (row) => (row.type === "group" ? row.group.displayName : getFeatureDisplayName(row.definition)),
        bold: true,
        wrap: true,
      },
      {
        header: "Default Value",
        accessor: (row) => {
          if (row.type === "feature") {
            return settingToDisplayValue(row.definition.defaultValue);
          }

          const values = new Set(row.visibleMembers.map((member) => member.definition.defaultValue));
          if (values.size === 1) {
            return settingToDisplayValue(row.visibleMembers[0]?.definition.defaultValue ?? false);
          }

          return "Mixed";
        },
        wrap: true,
      },
      {
        header: "Status",
        accessor: (row) => (row.type === "group" ? "group" : row.definition.stage),
        cell: ({ row }) => {
          if (row.original.type === "group") {
            return (
              <Capsule
                label={`${row.original.visibleMembers.length} flags`}
                size="small"
                backgroundColor="stone"
                color="white"
              />
            );
          }

          const mapping = mapFeatureStatusToDisplayTextAndColor(row.original.definition.stage);
          return <Capsule label={mapping.text} size="small" backgroundColor={mapping.color} color="white" />;
        },
        wrap: true,
      },
    ],
    [],
  );

  const statusFilterOptions: OptionProps<string>[] = [
    { label: "All statuses", value: "all" },
    { label: "Experimental", value: "experimental" },
    { label: "Alpha", value: "alpha" },
    { label: "Closed Beta", value: "closed_beta" },
    { label: "Open Beta", value: "open_beta" },
    { label: "GA", value: "general_availability" },
    { label: "Deprecated", value: "deprecated" },
  ];

  const environmentFilterOptions: OptionProps<string>[] = [
    { label: "All env settings", value: "all" },
    { label: "Enabled", value: "enabled" },
    { label: "Disabled", value: "disabled" },
    { label: "Default", value: "default" },
  ];

  const stalenessFilterOptions: OptionProps<string>[] = [
    { label: "All staleness", value: "all" },
    { label: "Fresh", value: "fresh" },
    { label: "Aging", value: "aging" },
    { label: "Stale", value: "stale" },
  ];

  const lifecycleFilterOptions: OptionProps<string>[] = [
    { label: "Active flags", value: "active" },
    { label: "Retired flags", value: "retired" },
  ];

  // Definitions passing every non-text filter (status, lifecycle, environment, staleness, hide-when-disabled).
  // Search is applied separately so group members can be surfaced via either a member match or a group-name match.
  const structurallyFilteredFeatures = useMemo(() => {
    const definitions = featureDefinitions ?? [];

    return definitions.filter((definition) => {
      const envSetting = definition.environmentOverrides.find((item) => item.environment === environment);
      let currentEnvironmentSetting = "default";
      if (envSetting?.value === true) {
        currentEnvironmentSetting = "enabled";
      }
      if (envSetting?.value === false) {
        currentEnvironmentSetting = "disabled";
      }

      if (statusFilter !== "all" && definition.stage !== statusFilter) {
        return false;
      }

      if (lifecycleFilter === "active" && definition.retiredAt) {
        return false;
      }

      if (lifecycleFilter === "retired" && !definition.retiredAt) {
        return false;
      }

      if (environmentFilter !== "all" && currentEnvironmentSetting !== environmentFilter) {
        return false;
      }

      const featureStaleness = flagData?.featureFlags?.[definition.key]?.stalenessLabel;
      if (stalenessFilter !== "all" && featureStaleness !== stalenessFilter) {
        return false;
      }

      if (definition.hideWhenDisabled && envSetting?.value === false) {
        return false;
      }

      return true;
    });
  }, [environment, environmentFilter, featureDefinitions, flagData, lifecycleFilter, stalenessFilter, statusFilter]);

  const filteredFeatures = useMemo(
    () => filterList(structurallyFilteredFeatures, search),
    [structurallyFilteredFeatures, search],
  );

  const featureListRows = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    // While searching, show a flat, de-duplicated list of matching flags. Nesting matches under every
    // group they belong to would repeat a flag once per group, so grouping is only applied when browsing
    // without a search term.
    if (normalizedSearch.length > 0) {
      const searchRows: FeatureFlagListRow[] = filteredFeatures.map((definition) => ({
        type: "feature",
        id: definition.key,
        definition,
      }));
      return searchRows;
    }

    const groups = featureFlagGroups ?? [];
    const groupedFeatureKeys = new Set(groups.flatMap((group) => group.members.map((member) => member.featureKey)));
    const visibleFeatureKeys = new Set(filteredFeatures.map((definition) => definition.key));
    const rows: FeatureFlagListRow[] = [];

    for (const group of groups) {
      const visibleMembers = group.members.filter((member) => visibleFeatureKeys.has(member.featureKey));
      if (visibleMembers.length > 0) {
        rows.push({ type: "group", id: group.id, group, visibleMembers });
      }
    }

    for (const definition of filteredFeatures) {
      if (!groupedFeatureKeys.has(definition.key)) {
        rows.push({ type: "feature", id: definition.key, definition });
      }
    }

    return rows;
  }, [featureFlagGroups, filteredFeatures, search]);

  // Keep the selected group in sync with the `group` query param (covers refresh and browser back/forward).
  useEffect(() => {
    setSelectedGroupId(group ?? null);
    if (group) {
      setGroupTab("settings");
    }
  }, [group]);

  useEffect(() => {
    if (featureDefinitionsLoading) {
      return;
    }

    if (!featureDefinitions || featureDefinitions.length === 0 || selectedGroupId || group) {
      if (selectedFeature !== null) {
        setSelectedFeature(null);
      }
      return;
    }

    if (mobile && !feature) {
      if (selectedFeature !== null) {
        setSelectedFeature(null);
      }
      return;
    }

    const selectedFromUrl = featureDefinitions.find((item) => item.key === feature);
    const nextFeature = selectedFromUrl?.key ?? featureDefinitions[0].key;
    if (selectedFeature !== nextFeature) {
      setSelectedFeature(nextFeature);
    }
  }, [
    feature,
    featureDefinitions,
    featureDefinitionsLoading,
    group,
    mobile,
    selectedFeature,
    selectedGroupId,
    setSelectedFeature,
  ]);

  const refreshFeatureDefinitions = async (): Promise<void> => {
    await mutateFeatureDefinitions();
  };

  const handleDeleteRetiredFlag = async (): Promise<void> => {
    if (!selectedFeatureDefinition) {
      return;
    }

    setIsDeletingRetired(true);
    const [, message] = await deleteFeatureFlag(selectedFeatureDefinition.key);
    setIsDeletingRetired(false);

    if (message) {
      return;
    }

    setShowDeleteRetiredModal(false);
    await refreshFeatureDefinitions();
    void navigate({ to: location.pathname });
  };

  const handleDeleteFeatureFlagGroup = async (): Promise<void> => {
    if (!selectedFeatureFlagGroup) {
      return;
    }

    setIsDeletingGroup(true);
    const [, message] = await deleteFeatureFlagGroup(selectedFeatureFlagGroup.id);
    setIsDeletingGroup(false);

    if (message) {
      return;
    }

    setShowDeleteGroupModal(false);
    setSelectedGroupId(null);
    await mutateFeatureFlagGroups();
    void navigate({ to: location.pathname });
  };

  let leftContent: ReactElement;
  if (leftPaneLoading) {
    leftContent = <Loading />;
  } else if (featureListRows.length > 0) {
    leftContent = (
      <ConfigTable
        columns={listColumns}
        data={featureListRows}
        onRowClick={(row) => {
          if (row.original.type === "group") {
            setSelectedGroupId(row.original.group.id);
            setGroupTab("settings");
            setSelectedFeature(null);
            void navigate({ to: `${location.pathname}?group=${row.original.group.id}` });
          } else {
            setSelectedGroupId(null);
            void navigate({ to: `${location.pathname}?feature=${row.original.definition.key}` });
          }
          if (mobile) {
            setMobilePane("detail");
          }
        }}
        isRowSelected={(row) => {
          if (row.original.type === "group") {
            return selectedGroupId === row.original.group.id;
          }

          return selectedFeature === row.original.definition.key;
        }}
        getRowCanExpand={(row) => row.original.type === "group" && row.original.visibleMembers.length > 0}
        subTable={(row) => {
          if (row.original.type === "feature") {
            return null;
          }

          return (
            <ConfigTable
              columns={featureColumns}
              data={row.original.visibleMembers.map((member) => member.definition)}
              showColumnHeaders={false}
              onRowClick={(subRow) => {
                setSelectedGroupId(null);
                void navigate({ to: `${location.pathname}?feature=${subRow.original.key}` });
                if (mobile) {
                  setMobilePane("detail");
                }
              }}
              isRowSelected={(subRow) => selectedFeature === subRow.original.key}
            />
          );
        }}
      />
    );
  } else {
    leftContent = (
      <BuilderPortalEmptyCTA>
        <div className="cta-container">
          <div className="cta-title">No features found.</div>
        </div>
      </BuilderPortalEmptyCTA>
    );
  }

  assertIsNotNullOrUndefined(user);

  const leftPane = (
    <div className={featureFlagListStyles.wrapper}>
      <AdminTableHeader
        title="Feature Flags"
        search={
          <SearchInput
            type="text"
            inputSize="medium"
            placeholder="Search"
            clearSearch={() => setSearch("")}
            onChange={(e) => setSearch(e.currentTarget.value)}
            value={search}
            isInHeader
          />
        }
      />
      {environment !== "prod" ? (
        <Message variant="inverted" messageType="warning" marginBottom>
          In non-production environments, Product, Engineering, and QA team members with an admin role can manage
          feature flag settings.
        </Message>
      ) : null}
      <p className={styles.description}>
        Feature flag defaults are managed in the feature flag service, and can be overridden at the environment,
        builder, or user level.{" "}
        <Link target="_blank" rel="noreferrer" className={styles.descriptionLink} href={DOC_URLS.FEATURE_FLAGS}>
          Read the documentation for more information
        </Link>
        .
      </p>
      <div style={{ marginBottom: 12 }}>
        <Button btnStyle="secondary" btnSize="small" onClick={() => setShowNewGroupModal(true)}>
          New Group
        </Button>
      </div>
      <div className={styles.filtersRow}>
        <Select
          options={lifecycleFilterOptions}
          value={lifecycleFilter}
          onChange={(selection) => {
            if (typeof selection === "string") {
              setLifecycleFilter(selection);
            }
          }}
          selectSize="medium"
        />
        <Select
          options={statusFilterOptions}
          value={statusFilter}
          onChange={(selection) => {
            if (typeof selection === "string") {
              setStatusFilter(selection);
            }
          }}
          selectSize="medium"
        />
      </div>
      <div className={styles.filtersRow}>
        <Select
          options={environmentFilterOptions}
          value={environmentFilter}
          onChange={(selection) => {
            if (typeof selection === "string") {
              setEnvironmentFilter(selection);
            }
          }}
          selectSize="medium"
        />
        <Select
          options={stalenessFilterOptions}
          value={stalenessFilter}
          onChange={(selection) => {
            if (typeof selection === "string") {
              setStalenessFilter(selection);
            }
          }}
          selectSize="medium"
        />
      </div>
      {leftContent}
    </div>
  );

  let rightPaneTitle = "";
  if (selectedFeatureFlagGroup) {
    rightPaneTitle = selectedFeatureFlagGroup.displayName;
  } else if (selectedFeatureDefinition) {
    rightPaneTitle = getFeatureDisplayName(selectedFeatureDefinition);
  } else if (selectedFeature) {
    rightPaneTitle = camelCaseToTitleCase(selectedFeature);
  }

  const rightPane =
    selectedFeature || selectedFeatureFlagGroup ? (
      <div className={featureFlagOverviewStyles.wrapper}>
        {mobile && mobilePane === "detail" ? (
          <div className={styles.mobileBackRow}>
            <Link className={styles.backTextLink} onClick={() => setMobilePane("list")}>
              <IconTriangle size={8} rotate={-90} />
              <span>Back to Flags</span>
            </Link>
          </div>
        ) : null}
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>{rightPaneTitle}</h1>
            {selectedFeatureFlagGroup ? (
              <div className={styles.groupDescription}>{selectedFeatureFlagGroup.description ?? FALLBACK_VALUE}</div>
            ) : null}
          </div>
          {selectedFeatureFlagGroup ? (
            <Capsule
              color="white"
              backgroundColor="stone"
              label={`${selectedFeatureFlagGroup.members.length} flags`}
              size="small"
            />
          ) : (
            <Capsule
              color="white"
              backgroundColor={mapFeatureStatusToDisplayTextAndColor(selectedFeatureDefinition?.stage ?? "").color}
              label={mapFeatureStatusToDisplayTextAndColor(selectedFeatureDefinition?.stage ?? "").text}
              size="small"
            />
          )}
        </div>
        {selectedFeatureFlagGroup ? (
          <>
            <div className={styles.tabRow}>
              <TabbedMenu menuItems={groupMenuItems} />
            </div>
            <HorizontalRule />
            {groupTab === "settings" ? (
              <div className={styles.groupTabContent}>
                <FeatureFlagGroupEditor
                  group={selectedFeatureFlagGroup}
                  featureDefinitions={featureDefinitions ?? []}
                  mode="settings"
                  footerStart={
                    <Button btnStyle="remove" btnSize="medium" onClick={() => setShowDeleteGroupModal(true)}>
                      Delete group
                    </Button>
                  }
                  onSaved={async (group) => {
                    await mutateFeatureDefinitions();
                    await mutateFeatureFlagGroups();
                    setSelectedGroupId(group.id);
                  }}
                />
              </div>
            ) : null}
            {groupTab === "members" ? (
              <div className={styles.groupTabContent}>
                <FeatureFlagGroupEditor
                  group={selectedFeatureFlagGroup}
                  featureDefinitions={featureDefinitions ?? []}
                  mode="members"
                  onSaved={async (group) => {
                    await mutateFeatureFlagGroups();
                    setSelectedGroupId(group.id);
                  }}
                />
              </div>
            ) : null}
            {groupTab === "builders" || groupTab === "users" ? (
              <div className={styles.groupTabContent}>
                <FeatureFlagGroupBulkControls
                  group={selectedFeatureFlagGroup}
                  section={groupTab}
                  builders={builders}
                  users={users}
                  buildersLoading={buildersLoading}
                  usersLoading={usersLoading}
                  setUserSearch={setUserSearch}
                  onBuildersUpdated={mutateBuilders}
                  onUsersUpdated={mutateUsers}
                  pagination={pagination}
                  setPagination={setPagination}
                  orderBy={orderBy}
                  setOrderBy={setOrderBy}
                />
              </div>
            ) : null}
          </>
        ) : (
          <>
            <div className={styles.meta}>
              {selectedFeatureDefinition?.retiredAt ? (
                <Button
                  disabled={!userIsSystemAdmin(user)}
                  title="Only System Admins can delete retired feature flags."
                  btnStyle="remove"
                  btnSize="small"
                  onClick={() => setShowDeleteRetiredModal(true)}
                >
                  Delete retired flag
                </Button>
              ) : null}
              <span className={styles.metaOwnerText}>{selectedFeatureDefinition?.ownerName ?? FALLBACK_VALUE}</span>
              <span className={styles.divider} />
              <span className={styles.metaText}>{selectedFeatureDefinition?.description ?? FALLBACK_VALUE}</span>
              {selectedFeatureDefinition?.documentationUrl ? (
                <>
                  <span className={styles.divider} />
                  <Link
                    target="_blank"
                    rel="noreferrer"
                    className={styles.metaDocumentationLink}
                    href={selectedFeatureDefinition.documentationUrl}
                  >
                    View Documentation
                  </Link>
                </>
              ) : null}
            </div>
            {selectedFeatureDefinition && canAnyHigharcStaffManageOverrides(selectedFeatureDefinition.stage) ? (
              <Message variant="inverted" messageType="info" marginBottom>
                This flag is in {mapFeatureStatusToDisplayTextAndColor(selectedFeatureDefinition.stage).text} and can be
                enabled/disabled for builders &amp; users by any Higharc staff member.
              </Message>
            ) : null}
            <div className={styles.tabRow}>
              <TabbedMenu menuItems={menuItems} />
            </div>
            <HorizontalRule />
            {rightPaneLoading || (!selectedFeatureDefinition && !featureDefinitionsLoading) ? (
              <Loading />
            ) : (
              <>
                {tab === "settings" && selectedFeatureDefinition && (
                  <div className={styles.description}>
                    {selectedFeatureDefinition.canEdit ? (
                      <FeatureFlagDefinitionEditor
                        selectedFeatureDefinition={selectedFeatureDefinition}
                        onDefinitionUpdated={refreshFeatureDefinitions}
                        groupSiblingKeys={selectedFeatureGroupSiblingKeys}
                      />
                    ) : (
                      <div className={styles.readOnlySettings}>
                        <p className={styles.readOnlyMessage}>
                          You no longer have edit access for this feature flag. Settings remain visible in read-only
                          mode.
                        </p>
                        <div className={styles.readOnlyGrid}>
                          <div>
                            <span className={styles.readOnlyLabel}>Owner</span>
                            <div>{selectedFeatureDefinition.ownerName ?? FALLBACK_VALUE}</div>
                            <div className={styles.readOnlySubtext}>
                              {selectedFeatureDefinition.ownerEmail ?? FALLBACK_VALUE}
                            </div>
                          </div>
                          <div>
                            <span className={styles.readOnlyLabel}>Display name</span>
                            <div>{selectedFeatureDefinition.displayName ?? FALLBACK_VALUE}</div>
                          </div>
                          <div>
                            <span className={styles.readOnlyLabel}>Status</span>
                            <div>{mapFeatureStatusToDisplayTextAndColor(selectedFeatureDefinition.stage).text}</div>
                          </div>
                          <div>
                            <span className={styles.readOnlyLabel}>Environment setting</span>
                            <div>{selectedFeatureEnvironmentSetting}</div>
                          </div>
                          <div>
                            <span className={styles.readOnlyLabel}>Default setting</span>
                            <div>{settingToDisplayValue(selectedFeatureDefinition.defaultValue)}</div>
                          </div>
                          <div>
                            <span className={styles.readOnlyLabel}>Allow per-user overrides</span>
                            <div>{settingToDisplayValue(selectedFeatureDefinition.allowPerUserEnablement)}</div>
                          </div>
                        </div>
                        <div>
                          <span className={styles.readOnlyLabel}>Description</span>
                          <div>{selectedFeatureDefinition.description ?? FALLBACK_VALUE}</div>
                        </div>
                        <div>
                          <span className={styles.readOnlyLabel}>Managers allowlist</span>
                          {selectedFeatureDefinition.editorAllowlistEmails.length > 0 ? (
                            <ul className={styles.readOnlyList}>
                              {selectedFeatureDefinition.editorAllowlistEmails.map((email) => {
                                return <li key={email}>{email}</li>;
                              })}
                            </ul>
                          ) : (
                            <div>{FALLBACK_VALUE}</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {tab === "builders" && (
                  <BuilderFeatureFlagsList
                    builders={ensureDefined(builders)}
                    selectedFeatureDefinition={selectedFeatureDefinition}
                    onUpdate={mutateBuilders}
                  />
                )}
                {tab === "users" && (
                  <UserFeatureFlagsList
                    users={ensureDefined(users)}
                    selectedFeatureDefinition={selectedFeatureDefinition}
                    onUpdate={mutateUsers}
                    setUserSearch={setUserSearch}
                    pagination={pagination}
                    setPagination={setPagination}
                    orderBy={orderBy}
                    setOrderBy={setOrderBy}
                  />
                )}
                {tab === "metrics" && (
                  <>
                    {selectedFeatureDefinition && selectedFeatureMetrics ? (
                      <>
                        <div className={styles.metricsGrid}>
                          {METRIC_CARD_CONFIG.map((card) => {
                            return (
                              <div key={card.key} className={styles.metricCard}>
                                <div className={styles.metricLabel}>
                                  <TextWithTooltipIcon text={card.label} tooltip={card.tooltip} />
                                </div>
                                <div className={styles.metricValue}>{selectedFeatureMetrics[card.key]}</div>
                              </div>
                            );
                          })}
                        </div>
                        <div className={styles.stalenessCard}>
                          <TextWithTooltipIcon
                            text="Staleness score"
                            tooltip="Score is calculated server-side from days since the last stage change, normalized by stage freshness windows: Experimental 14d, Alpha 30d, Closed Beta 45d, Open Beta 60d, General Availability 120d, Deprecated 30d."
                          />
                          <div className={styles.metricValue}>{selectedFeatureMetrics.stalenessScore}</div>
                          <div className={styles.metricSubtext}>
                            {camelCaseToTitleCase(selectedFeatureMetrics.stalenessLabel)} -{" "}
                            {selectedFeatureMetrics.stalenessDaysSinceStageUpdated} days since stage update
                          </div>
                        </div>
                      </>
                    ) : (
                      <BuilderPortalEmptyCTA>
                        <div className="cta-container">
                          <div className="cta-title">No metrics available for this feature yet.</div>
                        </div>
                      </BuilderPortalEmptyCTA>
                    )}
                  </>
                )}
                {tab === "history" && selectedFeatureDefinition && (
                  <FeatureFlagEventsList featureFlagKey={selectedFeatureDefinition.key} />
                )}
              </>
            )}
          </>
        )}
      </div>
    ) : (
      <div className={styles.emptyBox} />
    );

  const renderV2Layout = () => {
    let hideSide: "first" | "last" | undefined;
    if (mobile) {
      if (mobilePane === "list") {
        hideSide = "last";
      } else {
        hideSide = "first";
      }
    }

    return (
      <SplitFlex
        className={styles.splitLayout}
        canResize={!mobile}
        responsive={false}
        hideSide={hideSide}
        sizeRatio={0.35}
        firstMinSize={280}
        lastMinSize={340}
      >
        {leftPane}
        {rightPane}
      </SplitFlex>
    );
  };

  return (
    <AdminPage currentPage="features" pageTitle="Admin - Feature Flags">
      {renderV2Layout()}
      {showDeleteRetiredModal && selectedFeatureDefinition ? (
        <ConfirmDeleteModal
          onClose={() => setShowDeleteRetiredModal(false)}
          onConfirm={async (e) => {
            e.preventDefault();
            await handleDeleteRetiredFlag();
          }}
          isDeleting={isDeletingRetired}
          entityType="retired feature flag"
          entityName={getFeatureDisplayName(selectedFeatureDefinition)}
          message={
            <div>
              Are you sure you want to permanently delete this retired flag from the database? This cannot be undone.
            </div>
          }
        />
      ) : null}
      {showDeleteGroupModal && selectedFeatureFlagGroup ? (
        <ConfirmDeleteModal
          onClose={() => setShowDeleteGroupModal(false)}
          onConfirm={async (e) => {
            e.preventDefault();
            await handleDeleteFeatureFlagGroup();
          }}
          isDeleting={isDeletingGroup}
          entityType="feature flag group"
          entityName={selectedFeatureFlagGroup.displayName}
          message={
            <div>
              Are you sure you want to permanently delete this feature flag group? This only deletes the group; the
              underlying feature flags and their settings will not be deleted or changed.
            </div>
          }
        />
      ) : null}
      <Modal
        open={showNewGroupModal}
        onClose={() => setShowNewGroupModal(false)}
        title="New Feature Flag Group"
        size="medium"
        variants={["bodyPadding"]}
      >
        <FeatureFlagGroupEditor
          group={null}
          featureDefinitions={featureDefinitions ?? []}
          onSaved={async (group) => {
            await mutateFeatureFlagGroups();
            setShowNewGroupModal(false);
            setSelectedGroupId(group.id);
            setGroupTab("settings");
            setSelectedFeature(null);
            void navigate({ to: `${location.pathname}?group=${group.id}` });
          }}
        />
      </Modal>
    </AdminPage>
  );
}

export const Route = createFileRoute("/admin/features")({
  component: withAuthentication(AdminFeatureFlagsPage, { superAdmin: true }),
});
